import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ArrowUp, ArrowDown, Hash, FileText, DollarSign, Building } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { dataService, RawMaterial } from '../../services/dataService';

interface StockTransactionModalProps {
  material: RawMaterial;
  action: 'in' | 'out';
  onClose: () => void;
  onSuccess: () => void;
}

const StockTransactionModal: React.FC<StockTransactionModalProps> = ({ 
  material, 
  action, 
  onClose, 
  onSuccess 
}) => {
  const { tenantData, userData } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    quantity: 0,
    unit: material.subUnit || material.baseUnit, // Default to sub-unit
    reason: '',
    notes: '',
    supplier: action === 'in' ? material.supplier : '',
    costPerUnit: action === 'in' ? (material.costPerSubUnit || material.costPerUnit) : 0,
    referenceNumber: ''
  });

  const stockInReasons = [
    'Purchase from supplier',
    'Stock replenishment',
    'Return from customer',
    'Transfer from another location',
    'Inventory adjustment',
    'Other'
  ];

  const stockOutReasons = [
    'Used in production',
    'Sold to customer',
    'Damaged/Expired',
    'Transfer to another location',
    'Inventory adjustment',
    'Waste/Spillage',
    'Other'
  ];

  const reasons = action === 'in' ? stockInReasons : stockOutReasons;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.quantity <= 0) {
      setError('Quantity must be greater than 0');
      return;
    }

    if (!formData.reason.trim()) {
      setError('Please select a reason for this transaction');
      return;
    }

    if (action === 'out' && formData.quantity > material.currentStock) {
      // Convert current stock to the selected unit for error message
      let availableInSelectedUnit = material.currentStock;
      if (formData.unit === material.mainUnit && material.subUnitsPerMainUnit && material.subUnitsPerMainUnit > 1) {
        availableInSelectedUnit = Math.floor(material.currentStock / material.subUnitsPerMainUnit);
      }
      setError(`Cannot remove ${formData.quantity} ${formData.unit}. Only ${availableInSelectedUnit} ${formData.unit} available.`);
      return;
    }

    try {
      setLoading(true);
      setError('');

      if (action === 'in') {
        await dataService.addRawMaterialStock(
          tenantData!.id,
          material.id!,
          formData.quantity,
          formData.unit,
          formData.reason,
          userData!.firstName + ' ' + userData!.lastName,
          formData.supplier || undefined,
          formData.costPerUnit || undefined,
          formData.notes || undefined
        );
      } else {
        await dataService.removeRawMaterialStock(
          tenantData!.id,
          material.id!,
          formData.quantity,
          formData.unit,
          formData.reason,
          userData!.firstName + ' ' + userData!.lastName,
          formData.notes || undefined
        );
      }

      onSuccess();
    } catch (error: any) {
      console.error('Error processing stock transaction:', error);
      setError(error.message || 'Failed to process transaction. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Calculate new stock in base units (sub-units)
  const getBaseQuantity = () => {
    if (formData.unit === material.mainUnit && material.subUnitsPerMainUnit && material.subUnitsPerMainUnit > 1) {
      return formData.quantity * material.subUnitsPerMainUnit;
    }
    return formData.quantity;
  };

  const baseQuantity = getBaseQuantity();
  const totalCost = action === 'in' ? formData.quantity * formData.costPerUnit : 0;
  const newStock = action === 'in' 
    ? material.currentStock + baseQuantity 
    : material.currentStock - baseQuantity;

  const modalContent = (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg max-h-[95vh] overflow-y-auto border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg ${action === 'in' ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
              {action === 'in' ? (
                <ArrowUp className="w-6 h-6 text-green-600 dark:text-green-400" />
              ) : (
                <ArrowDown className="w-6 h-6 text-red-600 dark:text-red-400" />
              )}
            </div>
            <div>
              <h2 className="text-xl tablet:text-2xl font-bold text-gray-900 dark:text-white">
                Stock {action === 'in' ? 'In' : 'Out'}
              </h2>
              <p className="text-gray-600 dark:text-gray-300">{material.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-red-700 dark:text-red-300">{error}</p>
            </div>
          )}

          {/* Current Stock Info */}
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Current Stock Information</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600 dark:text-gray-300">Current Stock:</span>
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white">{material.currentStock} {material.subUnit || material.baseUnit}</p>
                  {material.subUnit && material.mainUnit && material.subUnitsPerMainUnit && material.subUnitsPerMainUnit > 1 && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      = {Math.floor(material.currentStock / material.subUnitsPerMainUnit)} {material.mainUnit}s + {material.currentStock % material.subUnitsPerMainUnit} {material.subUnit}s
                    </p>
                  )}
                </div>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-300">Min Stock:</span>
                <p className="font-semibold text-gray-900 dark:text-white">{material.minStock} {material.subUnit || material.baseUnit}</p>
              </div>
            </div>
            
            {material.subUnit && material.mainUnit && material.subUnitsPerMainUnit && material.subUnitsPerMainUnit > 1 && (
              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  <strong>Conversion:</strong> 1 {material.mainUnit} = {material.subUnitsPerMainUnit} {material.subUnit}s
                </p>
              </div>
            )}
          </div>

          {/* Transaction Details */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Quantity *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Hash className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  </div>
                  <input
                    type="number"
                    name="quantity"
                    required
                    min="0.01"
                    step="0.01"
                    value={formData.quantity}
                    onChange={handleInputChange}
                    className="input-field pl-10"
                    placeholder="0"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Unit *
                </label>
                <select
                  name="unit"
                  value={formData.unit}
                  onChange={handleInputChange}
                  className="input-field"
                >
                  {(() => {
                    const units = [];
                    
                    // Add sub-unit (primary unit)
                    if (material.subUnit) {
                      units.push({
                        value: material.subUnit,
                        label: material.subUnit
                      });
                    }
                    
                    // Add main unit if different from sub-unit and has conversion
                    if (material.mainUnit && 
                        material.mainUnit !== material.subUnit &&
                        material.subUnitsPerMainUnit && 
                        material.subUnitsPerMainUnit > 1) {
                      units.push({
                        value: material.mainUnit,
                        label: `${material.mainUnit} (${material.subUnitsPerMainUnit} ${material.subUnit}s)`
                      });
                    }
                    
                    // Fallback to base unit if no sub-unit system
                    if (units.length === 0 && material.baseUnit) {
                      units.push({
                        value: material.baseUnit,
                        label: material.baseUnit
                      });
                    }
                    
                    return units.map(unit => (
                      <option key={unit.value} value={unit.value}>
                        {unit.label}
                      </option>
                    ));
                  })()}
                </select>
              </div>
            </div>
            
            {/* Cost and Conversion Information */}
            {formData.quantity > 0 && (
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 border border-blue-200 dark:border-blue-800">
                <div className="grid grid-cols-1 tablet:grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">Cost per {formData.unit}:</span>
                    <span className="ml-2 text-gray-900 dark:text-white">
                      ₹{(() => {
                        if (formData.unit === material.subUnit) {
                          return (material.costPerSubUnit || material.costPerUnit || 0).toFixed(4);
                        } else if (formData.unit === material.mainUnit) {
                          return (material.costPerMainUnit || 0).toFixed(2);
                        }
                        return (material.costPerUnit || 0).toFixed(4);
                      })()}
                    </span>
                  </div>
                  
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">Total cost:</span>
                    <span className="ml-2 text-green-600 dark:text-green-400 font-semibold">
                      ₹{(() => {
                        let costPerUnit = 0;
                        if (formData.unit === material.subUnit) {
                          costPerUnit = material.costPerSubUnit || material.costPerUnit || 0;
                        } else if (formData.unit === material.mainUnit) {
                          costPerUnit = material.costPerMainUnit || 0;
                        } else {
                          costPerUnit = material.costPerUnit || 0;
                        }
                        return (costPerUnit * formData.quantity).toFixed(2);
                      })()}
                    </span>
                  </div>
                </div>
                
                {/* Conversion Information */}
                {material.subUnit && 
                 material.mainUnit && 
                 material.subUnitsPerMainUnit && 
                 material.subUnitsPerMainUnit > 1 && 
                 formData.unit === material.mainUnit && (
                  <div className="mt-2 pt-2 border-t border-blue-300 dark:border-blue-700">
                    <div className="text-xs text-blue-700 dark:text-blue-300">
                      <strong>Converting:</strong> {formData.quantity} {material.mainUnit}s = {formData.quantity * material.subUnitsPerMainUnit} {material.subUnit}s
                    </div>
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Reason *
              </label>
              <select
                name="reason"
                value={formData.reason}
                onChange={handleInputChange}
                className="input-field"
                required
              >
                <option value="">Select a reason</option>
                {reasons.map(reason => (
                  <option key={reason} value={reason}>
                    {reason}
                  </option>
                ))}
              </select>
            </div>

            {action === 'in' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Supplier
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Building className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                    </div>
                    <input
                      type="text"
                      name="supplier"
                      value={formData.supplier}
                      onChange={handleInputChange}
                      className="input-field pl-10"
                      placeholder="Supplier name"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Cost per Unit
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <DollarSign className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                    </div>
                    <input
                      type="number"
                      name="costPerUnit"
                      min="0"
                      step="0.01"
                      value={formData.costPerUnit}
                      onChange={handleInputChange}
                      className="input-field pl-10"
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Reference Number
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FileText className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                </div>
                <input
                  type="text"
                  name="referenceNumber"
                  value={formData.referenceNumber}
                  onChange={handleInputChange}
                  className="input-field pl-10"
                  placeholder="Invoice number, receipt, etc."
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                className="input-field"
                rows={3}
                placeholder="Additional notes about this transaction"
              />
            </div>
          </div>

          {/* Transaction Summary */}
          {formData.quantity > 0 && (
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Transaction Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-300">Current Stock:</span>
                  <span className="text-gray-900 dark:text-white">{material.currentStock} {material.subUnit || material.baseUnit}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-300">
                    {action === 'in' ? 'Adding:' : 'Removing:'}
                  </span>
                  <span className={action === 'in' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                    {action === 'in' ? '+' : '-'}{formData.quantity} {formData.unit}
                    {formData.unit === material.mainUnit && material.subUnitsPerMainUnit && material.subUnitsPerMainUnit > 1 && (
                      <span className="text-xs ml-1 text-gray-500 dark:text-gray-400">
                        (= {baseQuantity} {material.subUnit}s)
                      </span>
                    )}
                  </span>
                </div>
                <div className="flex justify-between font-semibold border-t border-gray-200 dark:border-gray-600 pt-2">
                  <span className="text-gray-900 dark:text-white">New Stock:</span>
                  <div className={newStock < material.minStock ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}>
                    <span>{newStock} {material.subUnit || material.baseUnit}</span>
                    {material.subUnit && material.mainUnit && material.subUnitsPerMainUnit && material.subUnitsPerMainUnit > 1 && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 font-normal">
                        = {Math.floor(newStock / material.subUnitsPerMainUnit)} {material.mainUnit}s + {newStock % material.subUnitsPerMainUnit} {material.subUnit}s
                      </div>
                    )}
                  </div>
                </div>
                {action === 'in' && totalCost > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-300">Total Cost:</span>
                    <span className="font-semibold text-gray-900 dark:text-white">₹{totalCost.toFixed(2)}</span>
                  </div>
                )}
                {newStock < material.minStock && (
                  <div className="text-red-600 dark:text-red-400 text-xs mt-2">
                    ⚠️ Warning: New stock level will be below minimum stock ({material.minStock} {material.subUnit || material.baseUnit})
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-4 pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || formData.quantity <= 0}
              className={`flex-1 ${
                action === 'in' 
                  ? 'bg-green-600 hover:bg-green-700 dark:bg-green-600 dark:hover:bg-green-700' 
                  : 'bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700'
              } text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {loading ? 'Processing...' : `Confirm Stock ${action === 'in' ? 'In' : 'Out'}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default StockTransactionModal; 